import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import { Typography, TextField, InputAdornment, IconButton, Tooltip, Grid } from "@material-ui/core";

import {
	Page,
	PageContent,
	ScrollToTopOnMount,
	Pagination,
	PaginationHeader,
	PageHeaderListActions,
} from "frame/components";
import SearchIcon from "@material-ui/icons/Search";
import ClearIcon from "@material-ui/icons/Clear";

// import { sortByPropertyDate } from 'utils'
import * as fromHealthSafetyForm from "features/health-safety-forms";
import HazardCard from "../cards/hazard-card";
import { EditHazardForm, PageHeaderTabs } from "features/health-safety-forms";
import { getPageResults } from "utils";

const useStyles = makeStyles((theme) => ({
	root: {},
	filter: { display: "flex" },
	search: {
		display: "flex"
	},
	gridContainer: {
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(3),
		[theme.breakpoints.down("xs")]: {
			marginTop: theme.spacing(3)
		}
	},
	filterGrid: {
		paddingTop: theme.spacing(1)
	},
}));

const HazardList = ({ role, hazards, onDelete, onGetHazardList }) => {
	const classes = useStyles();
	const [viewId, setViewId] = useState();
	const [editId, setEditId] = useState();
	const [searchFilter, setSearchFilter] = useState("");
	const [showFilters, setShowFilters] = useState(false);
	const isInitialMount = useRef(true);

	// const isDoneFetching = hazards.flag === 'done'
	const hazardList = [...getPageResults(hazards)].sort((a, b) => {
		const aActive = String(a.status).toLowerCase() === "active";
		const bActive = String(b.status).toLowerCase() === "active";
		return (bActive ? 1 : 0) - (aActive ? 1 : 0);
	});
	const empty = hazardList && hazardList.length === 0;

	const onView = (id) => () => setViewId(id);

	const onEdit = (id) => () => setEditId(id);

	const onCancelEdit = () => {
		setEditId(null);
		setViewId(null);
	};

	// Debounced search effect - triggers search after user stops typing
	useEffect(() => {
		// Skip debounce on initial mount
		if (isInitialMount.current) {
			isInitialMount.current = false;
			return;
		}

		const searchTimeout = setTimeout(() => {
			onGetHazardList({
				page: 1,
				pageSize: 20,
				orderBy: "createdAt",
				ascending: false,
				searchKey: searchFilter
			});
		}, 500); // 500ms debounce

		return () => clearTimeout(searchTimeout);
	}, [searchFilter, onGetHazardList]);

	return (
		<Page className={classes.root} title="Hazard Library">
			<ScrollToTopOnMount />
							<PageHeaderTabs
					// breadcrumbs="Hazards"
					title="Hazard Library"
					// addTabTitle="New"
					// listTabTitle="See All"
					addTabPath={fromHealthSafetyForm.ADD_HAZARD_PATH}
					hideListTabButton={true}
					staffUserHide={true}
					backActionPath={fromHealthSafetyForm.INFO_ROUTE}
				
						titleAdornment={
						<PageHeaderListActions
							showFilters={showFilters}
							onToggleFilters={() => setShowFilters(!showFilters)}
						/>
					}>
					{/* START: Search */}
					{showFilters && (
						<div className={classes.gridContainer}>
							<Grid container spacing={2} className={classes.filterGrid}>
								<Grid item xs={12} md={6}>
									<TextField
										className={classes.search}
										id="input-with-icon-textfield"
										variant="outlined"
										label="Search"
										InputProps={{
											startAdornment: (
												<InputAdornment position="start">
													<SearchIcon />
												</InputAdornment>
											),
											endAdornment: searchFilter && (
												<InputAdornment position="end">
													<IconButton
														aria-label="clear search"
														onClick={() => {
															setSearchFilter("");
															onGetHazardList({
																page: 1,
																pageSize: 20,
																orderBy: "createdAt",
																ascending: false,
																searchKey: ""
															});
														}}
														edge="end"
														size="small"
													>
														<ClearIcon />
													</IconButton>
												</InputAdornment>
											)
										}}
										value={searchFilter}
										onChange={(val) => {
											const { value } = val.target;
											setSearchFilter(value);
											// Real-time search with debounce effect will handle the API call
										}}
										style={{ backgroundColor: "white", maxWidth: 480, width: "100%" }}
										placeholder={"e.g. Name"}
									/>
								</Grid>
							</Grid>
						</div>
					)}
					{/* END: Search */}
				</PageHeaderTabs>
			<PageContent pad>
				<PaginationHeader hidePagination pagination={hazards} />

				{!empty &&
					hazardList.map((hazard, index) => (
						<HazardCard
							key={index}
							hazard={hazard}
							role={role}
							onView={onView(hazard.id)}
							onEdit={onEdit(hazard.id)}
							onDelete={onDelete}
						/>
					))}

				{empty && (
					<Typography variant="body1">
						There are currently no Hazards listed.
					</Typography>
				)}

				{/* {!isDoneFetching && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
            <CircularProgress />
          </div>
        )} */}

				<Pagination
					hidePagination
					onGetList={onGetHazardList}
					pagination={hazards}
					orderBy="createdAt"
					ascending={false}
					searchKey={searchFilter}
				/>
			</PageContent>
			{viewId && (
				<EditHazardForm id={viewId} readOnly onClose={onCancelEdit} />
			)}
			{editId && <EditHazardForm id={editId} onClose={onCancelEdit} />}
		</Page>
	);
};

HazardList.propTypes = {
	role: PropTypes.string.isRequired,
	hazards: PropTypes.object.isRequired,
	onDelete: PropTypes.func.isRequired,
	onGetHazardList: PropTypes.func.isRequired,
};

export default HazardList;
